import { AutocompleteChangeReason } from '@mui/base/AutocompleteUnstyled/useAutocomplete';
import { Alert } from '@mui/lab';
import { FormControl, FormHelperText, TextField, Theme } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import { makeStyles } from '@mui/styles';
import { observer } from 'mobx-react';
import React, { useState } from 'react';

import { ICommonProperties } from '../../../common/properties/ICommonProperties';

export interface IFormAutocompleteProperties extends ICommonProperties {
  values: AutocompleteSelectItem[];
  selected?: string | null;
  selectedDefault?: any;
  label: string | undefined;
  onChangeSelect: (option: { value: string; name?: string; key?: string; } | null, reason?: AutocompleteChangeReason) => void;
  errorMessage?: string;
  disabled?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  isLoading?: boolean;
  loadingText?: string;
  controlPadding?: string;
  required?: boolean;
  helperText?: string;
  size?: string;
  style?: any;
  variant?: 'outlined' | 'filled' | 'standard';
  warningMessage?: string | null;
  dataCy?: string;
  isNeedDescriprion?: boolean;
  isHaveErrors?: boolean;
  isDisableClearable?: boolean;
}

export interface AutocompleteSelectItem {
  value: any;
  name: string;
  icons?: { fragment: any; position: FormAutocompleteOptionIconPosition }[];
}

export enum FormAutocompleteOptionIconPosition {
  left = 'left',
  right = 'right',
}
export const FormAutocomplete = observer(
  ({
    values,
    label,
    onChangeSelect,
    selected = null,
    selectedDefault = undefined,
    errorMessage,
    disabled = false,
    onOpen,
    onClose,
    isLoading,
    controlPadding = undefined,
    loadingText = '',
    required = false,
    helperText = undefined,
    size = 'small',
    style = {},
    variant = 'outlined',
    warningMessage = '',
    dataCy,
    isNeedDescriprion = false,
    isDisableClearable = false,
  }: IFormAutocompleteProperties) => {
    const classes = useStyles({ controlPadding });
    const onChange: any = (event: any, value: { value: string } | null, reason?: AutocompleteChangeReason) => {
      value ? onChangeSelect(value, reason) : onChangeSelect(null);
      event.stopPropagation();
    };

    const selectedValue = values.find((value) => selected === value.value) || null;
    const [isOpen, changeIsOpen] = useState(false);
    const changeAutocompleteState = (newOpenState: boolean) => {
      if (isOpen === false && newOpenState !== isOpen) {
        onOpen && onOpen();
      }
      if (isOpen === true && newOpenState !== isOpen) {
        onClose && onClose();
      }
      changeIsOpen(newOpenState);
    };

    const lableWithDescription = (option) => {
      return `${option.name} (${option.description.replaceAll('&nbsp;', ' ')})`
    }
    return (
      <FormControl style={style} fullWidth className={classes.formControl}>
        <div className={classes.root}>
          {((selected && values.length > 0) || !selected) && (
            <Autocomplete
              disableClearable = {isDisableClearable}
              data-cy={dataCy}
              classes={{ option: classes.autocompleteOption }}
              disablePortal
              //@ts-ignore
              size={size}
              disabled={disabled}
              value={selectedValue || selectedDefault}
              color="secondary"
              options={values}
              getOptionLabel={(option) => isNeedDescriprion ? lableWithDescription(option) : option.name}
              onChange={onChange}
              loading={isLoading}
              loadingText={loadingText}
              onOpen={(event) => {
                // event.stopPropagation();
              }}
              // open={isOpen}
              isOptionEqualToValue={(option: any, value: any) => {
                if (value === null) return false;
                if (typeof value === 'string') return option.value === value;
                return option.value === value?.value;
              }}
              onClickCapture={function (event: any) {
                changeAutocompleteState(disabled ? false : !isOpen);
              }}
              onClose={() => {
                changeAutocompleteState(false);
              }}
              renderOption={(props: any, option: any) => {
                const leftIcons =
                  option.icons?.filter((icon: any) => icon.position === FormAutocompleteOptionIconPosition.left) || [];
                const rightIcons =
                  option.icons?.filter((icon: any) => icon.position === FormAutocompleteOptionIconPosition.right) || [];

                return (
                  <li {...props} key={option.value}
                    data-cy={`${option.name}`}
                  >
                    {leftIcons.map((icon: any) => icon.fragment)}
                    <div
                      style={{
                        paddingLeft: leftIcons.length > 0 ? '10px' : '0px',
                        paddingRight: rightIcons.length > 0 ? '10px' : '0px',
                      }}
                    >
                      {isNeedDescriprion ? lableWithDescription(option) : option.name}
                    </div>
                    {rightIcons.map((icon: any) => icon.fragment)}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  error={!!errorMessage}
                  helperText={errorMessage || null}
                  required={required}
                  color="secondary"
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                  label={label}
                  variant={variant}
                />
              )}
            />
          )}
          {/* {errorMessage && (
            <Alert icon={false} variant="outlined" severity="error">
              {errorMessage}
            </Alert>
          )} */}
        </div>
        <FormHelperText>{helperText}</FormHelperText>
        {warningMessage && (
          <Alert icon={false} variant="outlined" severity="warning">
            {warningMessage}
          </Alert>
        )}
      </FormControl>
    );
  },
);

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    '& > * + *': {
      marginTop: theme.spacing(3),
    },
  },
  formControl: {
    padding: (props: any) => (props.controlPadding ? props.controlPadding : theme.spacing(2)),
  },
  autocompleteOption: {
    minHeight: '40px',
  },
  disabled: {
    border: '1px solid white',
  },
}));
